// frontend/src/components/TemplateTester.jsx
import React, { useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext.jsx';
import logger from '../utils/logger';

function TemplateTester({ template, onClose }) {
  const { token } = useContext(AuthContext);
  const [context, setContext] = useState({});
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validation, setValidation] = useState(null);

  // Helper function to decode HTML entities
  const decodeHtmlEntities = (text) => {
    if (!text) return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  // Initialize context with template variables
  React.useEffect(() => {
    if (template?.variables) {
      const initialContext = {};
      template.variables.forEach(variable => {
        initialContext[variable.name] = variable.defaultValue || '';
      });
      setContext(initialContext);
    }
  }, [template]);

  const handlePreview = async () => {
    if (!template?._id) {
      setError('Template ID is required');
      return;
    }

    setLoading(true);
    setError('');
    setValidation(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/prompts/${template._id}/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ context })
      });

      if (!response.ok) {
        throw new Error('Failed to preview template');
      }

      const data = await response.json();
      // Decode HTML entities in the rendered template
      const decodedPreview = decodeHtmlEntities(data.rendered || '');
      setPreview(decodedPreview);
      setValidation(data.validation);
    } catch (err) {
      logger.error('Error previewing template:', err);
      setError(err.message || 'Failed to preview template');
    } finally {
      setLoading(false);
    }
  };

  const handleContextChange = (variableName, value) => {
    setContext(prev => ({
      ...prev,
      [variableName]: value
    }));
  };

  if (!template) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Test Template</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{template.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Variable Inputs */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Test Variables</h3>
            <div className="space-y-4">
              {template.variables && template.variables.length > 0 ? (
                template.variables.map(variable => (
                  <div key={variable.name}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {variable.name}
                      {variable.required && <span className="text-red-500 ml-1">*</span>}
                      {variable.description && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                          ({variable.description})
                        </span>
                      )}
                    </label>
                    <textarea
                      value={context[variable.name] || ''}
                      onChange={(e) => handleContextChange(variable.name, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      rows="3"
                      placeholder={variable.defaultValue || `Enter value for ${variable.name}`}
                    />
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No variables defined for this template.
                </p>
              )}
            </div>
          </div>

          {/* Preview Button */}
          <div className="mb-6">
            <button
              onClick={handlePreview}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Generating Preview...' : 'Generate Preview'}
            </button>
          </div>

          {/* Validation Results */}
          {validation && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Validation</h3>
              {validation.errors && validation.errors.length > 0 && (
                <div className="mb-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">Errors:</p>
                  <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-300">
                    {validation.errors.map((error, idx) => (
                      <li key={idx}>{error.message}</li>
                    ))}
                  </ul>
                </div>
              )}
              {validation.warnings && validation.warnings.length > 0 && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">Warnings:</p>
                  <ul className="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-300">
                    {validation.warnings.map((warning, idx) => (
                      <li key={idx}>{warning.message}</li>
                    ))}
                  </ul>
                </div>
              )}
              {validation.isValid && validation.errors.length === 0 && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <p className="text-sm text-green-800 dark:text-green-200">✓ Template is valid</p>
                </div>
              )}
            </div>
          )}

          {/* Preview Output */}
          {preview && (
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Rendered Preview</h3>
              <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md">
                <pre className="whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">
                  {preview}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TemplateTester;
